/**
 * Markdown renderer (Req 2.3).
 *
 * A tiny, SAFE markdown subset renderer that parses text into React elements.
 * It NEVER uses dangerouslySetInnerHTML and NEVER injects raw HTML — every
 * node is a React element, so arbitrary HTML in the source is rendered as
 * literal text, not executed.
 *
 * Supported subset:
 *  - headings: `#`, `##`, `###`
 *  - unordered lists: lines starting with `- `
 *  - paragraphs (blank-line separated) with inline formatting
 *  - inline: bold `**x**`, italic `*x*`, inline code `` `x` ``, links `[text](url)`
 *  - single newlines inside a paragraph become <br/>
 */
import type { ReactNode } from 'react';
import type { MarkdownPayload } from './types';

type InlineToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'italic'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; text: string; url: string };

/** Tokenize a single line of inline markdown. Order matters: code, link, bold, italic. */
function parseInline(line: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  // Combined matcher; each alternative is tried left-to-right at each position.
  const pattern = /(`[^`]+`)|(\[[^\]]*\]\([^)]*\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: line.slice(lastIndex, match.index) });
    }
    const matched = match[0];
    if (matched.startsWith('`')) {
      tokens.push({ type: 'code', value: matched.slice(1, -1) });
    } else if (matched.startsWith('[')) {
      const linkMatch = /^\[([^\]]*)\]\(([^)]*)\)$/.exec(matched);
      if (linkMatch) {
        tokens.push({ type: 'link', text: linkMatch[1] ?? '', url: linkMatch[2] ?? '' });
      } else {
        tokens.push({ type: 'text', value: matched });
      }
    } else if (matched.startsWith('**')) {
      tokens.push({ type: 'bold', value: matched.slice(2, -2) });
    } else {
      tokens.push({ type: 'italic', value: matched.slice(1, -1) });
    }
    lastIndex = match.index + matched.length;
  }
  if (lastIndex < line.length) {
    tokens.push({ type: 'text', value: line.slice(lastIndex) });
  }
  return tokens;
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  // Block javascript:/data:/vbscript: schemes; allow http(s), mailto, relative, anchors.
  return !/^(javascript|data|vbscript):/.test(trimmed);
}

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  return parseInline(line).map((tok, i) => {
    const key = `${keyPrefix}-${i}`;
    switch (tok.type) {
      case 'bold':
        return <strong key={key}>{tok.value}</strong>;
      case 'italic':
        return <em key={key}>{tok.value}</em>;
      case 'code':
        return (
          <code key={key} className="rounded bg-slate-800 px-1 py-0.5 text-[0.85em]">
            {tok.value}
          </code>
        );
      case 'link':
        return isSafeUrl(tok.url) ? (
          <a
            key={key}
            href={tok.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline"
          >
            {tok.text || tok.url}
          </a>
        ) : (
          <span key={key}>{tok.text || tok.url}</span>
        );
      case 'text':
      default:
        return <span key={key}>{tok.value}</span>;
    }
  });
}

/** Split source into blocks: headings, list groups, and paragraphs. */
function renderBlocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const idx = key++;
    const content: ReactNode[] = [];
    paragraph.forEach((ln, i) => {
      if (i > 0) content.push(<br key={`br-${idx}-${i}`} />);
      content.push(...renderInline(ln, `p-${idx}-${i}`));
    });
    blocks.push(
      <p key={`p-${idx}`} className="mb-3 leading-relaxed">
        {content}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const idx = key++;
    blocks.push(
      <ul key={`ul-${idx}`} className="mb-3 list-disc pl-5 space-y-1">
        {listItems.map((item, i) => (
          <li key={`li-${idx}-${i}`}>{renderInline(item, `li-${idx}-${i}`)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine;
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      const level = (heading[1] ?? '#').length;
      const body = heading[2] ?? '';
      const idx = key++;
      const cls =
        level === 1
          ? 'text-2xl font-bold mb-3'
          : level === 2
            ? 'text-xl font-semibold mb-2'
            : 'text-lg font-semibold mb-2';
      const inner = renderInline(body, `h-${idx}`);
      blocks.push(
        level === 1 ? (
          <h1 key={`h-${idx}`} className={cls}>
            {inner}
          </h1>
        ) : level === 2 ? (
          <h2 key={`h-${idx}`} className={cls}>
            {inner}
          </h2>
        ) : (
          <h3 key={`h-${idx}`} className={cls}>
            {inner}
          </h3>
        ),
      );
      continue;
    }

    const listItem = /^\s*-\s+(.*)$/.exec(line);
    if (listItem) {
      flushParagraph();
      listItems.push(listItem[1] ?? '');
      continue;
    }

    // plain text line: part of a paragraph
    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
}

export function Markdown({ title, payload, accent }: { title?: string; payload: MarkdownPayload; accent: string }) {
  const text = typeof payload.text === 'string' ? payload.text : '';

  return (
    <div className="rounded-lg bg-slate-900 p-5">
      {title ? (
        <h2 className="text-xl font-semibold mb-4" style={{ color: accent }}>
          {title}
        </h2>
      ) : null}
      <div className="text-sm text-slate-100">
        {text.trim() === '' ? <p className="text-slate-500">No content.</p> : renderBlocks(text)}
      </div>
    </div>
  );
}
